import * as React from 'react'
import ReactSelect from 'react-select'
import styled from 'styled-components'
import * as constants from 'static/constants'
import { Select } from 'components/atoms'
import { IJobSlot, IJobBulkPlanSlot, ISkill } from 'static/interfaces'
import SkillContainer from 'components/molecules/Weekly/SkillContainer'
import { StaticContext } from 'core/contexts/weekly/StaticContext'
import { skillCondition, convertLevelToRange } from 'utils/job'
import { Tooltip } from 'components/atoms/Tooltip'
import SvgIcon from 'components/atoms/SvgIcon'
import { IconName } from '../../../types/icon-names'

interface IProps {
  slot: IJobSlot | IJobBulkPlanSlot | null
  setSlots: React.Dispatch<React.SetStateAction<IJobSlot[] | IJobBulkPlanSlot[] | null>>
  editting: boolean
  skills?: ISkill[]
  index: number
}

const {
  SKILL_LEVELS,
  SKILL_CONDITION_TYPES,
  JOB_SKILL_LEVEL_MIN: MIN_LEVEL,
  JOB_SKILL_LEVEL_MAX: MAX_LEVEL,
} = constants

const JobSlots: React.VFC<IProps> = ({
  slot: initialSlot,
  setSlots,
  editting,
  skills: initialSkills,
  index,
}) => {
  const { getSkills } = React.useContext(StaticContext)
  const [slot, setSlot] = React.useState<IJobSlot | IJobBulkPlanSlot | null>(initialSlot)
  const [level, setLevel] = React.useState<number>(
    slot ? skillCondition(slot.tour_skill_min_level, slot.tour_skill_max_level).level : MIN_LEVEL
  )
  const [condition, setCondition] = React.useState<string>(
    slot ? skillCondition(slot.tour_skill_min_level, slot.tour_skill_max_level).condition : 'exact'
  )
  const skills = React.useMemo(() => {
    return initialSkills || getSkills()
  }, [initialSkills])

  const updateSlot = React.useCallback(
    (nextSlot: IJobSlot | IJobBulkPlanSlot | null) => {
      setSlot(nextSlot)
      if (!nextSlot) {
        return
      }
      setSlots(prev => {
        if (!prev) {
          return prev
        }
        const next = [...(prev as Array<IJobSlot | IJobBulkPlanSlot>)]
        next[index] = nextSlot
        return next as typeof prev
      })
    },
    [index, setSlots]
  )

  const handleLevelConditionChange = React.useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      if (!slot) {
        return
      }
      const _level = e.target.name === 'tour_skill_level' ? Number(e.target.value) : level
      const _condition = e.target.name === 'tour_skill_condition' ? e.target.value : condition
      const range = convertLevelToRange(String(_level), _condition)
      const { min_level: min, max_level: max } = range
      updateSlot({ ...slot, tour_skill_min_level: min, tour_skill_max_level: max })

      const conditions = skillCondition(min, max)
      setLevel(conditions.level)
      setCondition(conditions.condition)
    },
    [condition, level, slot, updateSlot]
  )

  const conditionOptions = React.useMemo(() => {
    const removeKeys = level === MIN_LEVEL ? ['lte', 'gte'] : level === MAX_LEVEL ? ['gte'] : []
    return Object.keys(SKILL_CONDITION_TYPES)
      .filter(key => !removeKeys.includes(key))
      .map(key => ({
        value: key,
        label: SKILL_CONDITION_TYPES[key],
      }))
  }, [level, MIN_LEVEL, MAX_LEVEL])

  const handleDelete = React.useCallback(() => {
    setSlots(prev => {
      if (!prev) {
        return prev
      }
      const next = (prev as Array<IJobSlot | IJobBulkPlanSlot>).filter(item => {
        if (!slot) {
          return true
        }
        if (slot.id != null && item.id != null) {
          return item.id !== slot.id
        }
        if ('client_uid' in slot && 'client_uid' in item) {
          return slot.client_uid !== item.client_uid
        }
        return item !== slot
      })
      return next as typeof prev
    })
  }, [setSlots, slot])

  React.useEffect(() => {
    if (!initialSlot) {
      return
    }
    setSlot(initialSlot)
    const conditions = skillCondition(
      initialSlot.tour_skill_min_level,
      initialSlot.tour_skill_max_level
    )
    setLevel(conditions.level)
    setCondition(conditions.condition)
  }, [initialSlot])

  return (
    <Wrapper>
      {editting ? (
        <div className="slotEdit">
          <div className="tourSkillRank">
            <Select
              name="tour_skill_level"
              options={Object.keys(SKILL_LEVELS)
                .filter(key => SKILL_LEVELS[key] !== 'NG')
                .map(key => ({
                  value: key,
                  label: SKILL_LEVELS[key],
                }))}
              value={String(level)}
              onChangeHandler={handleLevelConditionChange}
            />
            <Select
              name="tour_skill_condition"
              options={conditionOptions}
              value={condition}
              onChangeHandler={handleLevelConditionChange}
            />
          </div>
          <div className="tourSkillNumberContainer">
            <div className="tourSkillNumber">
              <label>
                <SvgIcon
                  name="icon-direct"
                  className="icon"
                  ariaHidden={true}
                  role="presentation"
                />
                先着順
              </label>
              <input
                required={true}
                name="direct_max_limit"
                type="text" // 上下キーなどで増減できないようnumberではなくtextにする
                inputMode="numeric"
                defaultValue={slot?.direct_max_limit || 0}
                placeholder="募集数"
                className="maxLimit"
                onChange={e => {
                  // 数値以外は許可しない
                  const numeric = e.target.value.replace(/[^0-9]/g, '')
                  e.target.value = numeric
                  if (slot) {
                    updateSlot({ ...slot, direct_max_limit: numeric === '' ? 0 : Number(numeric) })
                  }
                }}
              />
              <span>人</span>
            </div>
            <div className="tourSkillNumber">
              <label>
                <SvgIcon
                  name="icon-selection"
                  className="icon"
                  ariaHidden={true}
                  role="presentation"
                />
                承認制
              </label>
              <input
                required={true}
                name="selection_max_limit"
                type="text" // 上下キーなどで増減できないようnumberではなくtextにする
                inputMode="numeric"
                defaultValue={slot?.selection_max_limit || 0}
                placeholder="募集数"
                className="maxLimit"
                onChange={e => {
                  // 数値以外は許可しない
                  const numeric = e.target.value.replace(/[^0-9]/g, '')
                  e.target.value = numeric
                  if (slot) {
                    updateSlot({
                      ...slot,
                      selection_max_limit: numeric === '' ? 0 : Number(numeric),
                    })
                  }
                }}
              />
              <span>人</span>
            </div>
            <div className="tooltipContainer">
              <Tooltip
                position="left"
                content="「◯」などのツアースキルのランク、「以上」などのランクの範囲を選択し、先着順で募集する際は人数を設定、承認制で募集する際はチェックボックスにチェックを入れてください。先着順、承認制、両方が指定されている場合、先着順の募集数が集まり次第、募集形態が承認制に移行されます。"
              >
                <SvgIcon name="help" className="icon" ariaHidden={true} role="presentation" />
              </Tooltip>
            </div>
          </div>
          <div className="selectContainer">
            <ReactSelect
              classNamePrefix="skills"
              placeholder="スキル（最大3個まで指定可）"
              options={skills.map(skill => ({
                label: skill.name,
                value: String(skill.id),
              }))}
              isMulti={true}
              menuPosition="fixed"
              menuPlacement="bottom"
              onChange={selected =>
                slot &&
                updateSlot({
                  ...slot,
                  skill_ids: (selected || []).map(s => Number(s.value)),
                })
              }
              styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
              value={
                slot?.skill_ids?.map(id => ({
                  label: skills.find(skill => skill.id === id)?.name || '',
                  value: String(id),
                })) || []
              }
              menuPortalTarget={typeof window !== 'undefined' ? document.body : undefined}
              isOptionDisabled={(option, selectValue) =>
                selectValue.length >= 3 && !selectValue.some(v => v.value === option.value)
              }
            />
          </div>

          <div className="deleteSlotButton">
            <Tooltip content="詳細な設定を削除する">
              <button onClick={handleDelete} type="button">
                <SvgIcon
                  name="delete_forever"
                  className="icon"
                  ariaHidden={true}
                  role="presentation"
                />
              </button>
            </Tooltip>
          </div>
        </div>
      ) : (
        <div className="slotView">
          <div className="tourSkillRank">
            <SvgIcon
              name={`skill-level-rank-${level}` as IconName}
              className={`icon rank-${level}`}
              ariaHidden={true}
              role="presentation"
            />
            {SKILL_CONDITION_TYPES[condition]}
          </div>
          {slot?.direct_max_limit !== 0 && (
            <div className="tourSkillNumber">
              <span>
                <SvgIcon
                  name="icon-direct"
                  className="icon"
                  ariaHidden={true}
                  role="presentation"
                />
                先着順
              </span>
              <b>{slot?.direct_max_limit || 0}</b>
              <small>人</small>
            </div>
          )}
          {slot?.selection_max_limit !== 0 && (
            <div className="tourSkillNumber">
              <span>
                <SvgIcon
                  name="icon-selection"
                  className="icon"
                  ariaHidden={true}
                  role="presentation"
                />
                承認制
              </span>
              <b>{slot?.selection_max_limit || 0}</b>
              <small>人</small>
            </div>
          )}
          <div className="skills">
            {slot?.skill_ids.length > 0 && skills.length > 0 && (
              <SkillContainer
                type="skill"
                skills={slot.skill_ids.map(id => skills.find(s => s.id === id)) || []}
              />
            )}
          </div>
        </div>
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  .slotEdit {
    position: relative;
    display: flex;
    align-items: center;
    flex-wrap: wrap;

    gap: 8px;
    .maxLimit {
      width: 80px;
      height: 32px;
      padding: 4px 8px;
      border: 1px solid ${constants.COLORS.divider};
      border-radius: 4px;
      font-size: 14px;
      &:focus {
        outline: none;
        border-color: ${constants.COLORS.primary};
      }
    }
    .tourSkillRank {
      display: flex;
      align-items: center;
      gap: 4px;
    }
    .tourSkillNumberContainer {
      display: flex;
      align-items: center;
      gap: 4px;
    }
    .tourSkillNumber {
      position: relative;
      display: flex;
      align-items: center;
      label {
        display: flex;
        align-items: center;
        gap: 4px;
        height: 32px;
        padding: 0 8px;
        border: 1px solid ${constants.COLORS.divider};
        border-right: none;
        border-radius: 4px 0 0 4px;
        .icon {
          width: 16px;
          height: 16px;
        }
      }
      input {
        height: 32px;
        padding: 0 8px;
        border-radius: 0 4px 4px 0;
      }
      span {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        right: 8px;
        font-size: 12px;
      }
    }
    .tooltipContainer {
      .icon {
        width: 20px;
        height: 20px;
        color: ${constants.COLORS.subText};
        cursor: pointer;
      }
    }
    .tooltipContent {
      max-width: 320px;
      font-size: 12px;
      overflow-wrap: normal;
      word-break: break-word;
      text-align: left;
      justify-content: left;
    }
    .deleteSlotButton {
      position: absolute;
      top: 50%;
      right: -16px;
      transform: translateY(-50%);
      button {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        padding: 0;
        color: ${constants.COLORS.subText};
        background-color: ${constants.COLORS.lightGray};
        border: 1px solid ${constants.COLORS.divider};
        border-radius: 50%;
        cursor: pointer;
        transition: 0.2s color, 0.2s background-color, 0.2s border-color;
        .icon {
          width: 24px;
          height: 24px;
        }
        &:hover {
          color: ${constants.COLORS.white};
          background-color: ${constants.COLORS.subText};
          border-color: ${constants.COLORS.subText};
        }
      }
    }
  }
  .slotView {
    display: flex;
    align-items: center;
    gap: 4px 8px;
    color: ${constants.COLORS.black};
    font-size: 12px;
    border: 1px solid ${constants.COLORS.divider2};
    background-color: ${constants.COLORS.white};
    border-radius: 4px;
    padding: 4px;
    @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
      flex-wrap: wrap;
    }
    .tourSkillRank {
      display: flex;
      align-items: center;
      gap: 2px;
      .icon {
        width: 16px;
        height: 16px;
        &.rank-1 {
          color: ${constants.COLORS.danger};
        }
        &.rank-2 .icon,
        &.rank-3 {
          color: ${constants.COLORS.info2};
        }
        &.rank-4 {
          color: ${constants.COLORS.primary};
        }
        &.rank-5 {
          color: ${constants.COLORS.info3};
        }
      }
    }
    .tourSkillNumber {
      display: flex;
      align-items: center;
      gap: 2px;
      span {
        display: flex;
        align-items: center;
        gap: 2px;
        .icon {
          width: 16px;
          height: 16px;
        }
      }
      b {
        font-weight: 600;
      }
      small {
        font-size: 11px;
        color: ${constants.COLORS.subText};
      }
    }
    .skills {
      .skill {
        padding: 2px 4px;
      }
    }
  }
`

export default JobSlots
